import {useMemo} from 'react';

import {QueryResultType, useBenchlingQuery} from 'graphql-client/BenchlingQuery';
import {groupSchemasByType} from 'nav/sidepanel/listing/ItemListingUtil';
import {getOmniboxAllowedTypes} from 'omnibox/omniboxConstants';
import type {FilterTreeNode} from 'omnibox/omniboxFilterTree';
import {buildTypeTreeNodes} from 'omnibox/omniboxTypeOptions';
import type {TypesFilterSchemasQuery} from 'search/filters/__generated/TypesFilterSchemasQuery';
import type {TypesFilterTagSchemasSSIQuery} from 'search/filters/__generated/TypesFilterTagSchemasSSIQuery';
import {TYPES_FILTER_SCHEMAS_QUERY, TYPES_FILTER_SSI_QUERY} from 'search/filters/TypesAndSchemaFilterForm';

export interface OmniboxFilter {
  kind: 'type' | 'schema' | 'project' | 'folder' | 'user';
  id: string;
  name: string;
  modelType?: string;
  projectName?: string;
  handle?: string;
  isSelf?: boolean;
  // Global search types (as in normal search's Type filter) a Type dropdown selection narrows the
  // omnibox search to, sent as a narrowed `allowedTypes` rather than as `objectTypes`.
  searchTypes?: ReadonlyArray<string>;
  // For a type that search has no document type of its own for (e.g. Antibody, a kind of Custom
  // Entity): the schemas it is scoped to, sent as schema filters alongside `searchTypes`.
  schemaApiIds?: ReadonlyArray<string>;
  // The Type dropdown option a schema was picked from. A schema can be listed under several options
  // (e.g. a workflow task schema under both Task group and Workflow Task), and the row the user
  // picked is the one shown as selected.
  optionKey?: string;
}

export interface TypeaheadData {
  // Options for the Type dropdown: types grouped by product area, each with its schemas nested beneath.
  typeTreeNodes: Array<FilterTreeNode>;
}

export default function useTypeaheadData(): TypeaheadData {
  // Use the same schema queries as search's Type filter for full coverage.
  const schemasResult = useBenchlingQuery<TypesFilterSchemasQuery>(TYPES_FILTER_SCHEMAS_QUERY);
  const ssiResult = useBenchlingQuery<TypesFilterTagSchemasSSIQuery>(TYPES_FILTER_SSI_QUERY);
  const schemasData = schemasResult.type === QueryResultType.SUCCESS ? schemasResult.data : null;
  const ssiData = ssiResult.type === QueryResultType.SUCCESS ? ssiResult.data : undefined;

  // Active schemas keyed by the type they belong to, starred first, for the Type dropdown.
  const schemasByType = useMemo((): Map<string, Array<OmniboxFilter>> => {
    const byType = new Map<string, Array<OmniboxFilter>>();
    if (!schemasData) {
      return byType;
    }
    const starredIds = new Set(schemasData.starredTagSchemas.map((s) => s.id));
    for (const [modelType, schemas] of Object.entries(groupSchemasByType(schemasData, ssiData))) {
      const active = schemas.filter((s) => !('archiveRecord' in s && s.archiveRecord));
      const ordered = [
        ...active.filter((s) => starredIds.has(s.id)),
        ...active.filter((s) => !starredIds.has(s.id)),
      ];
      byType.set(
        modelType,
        ordered.map((s): OmniboxFilter => ({kind: 'schema', id: s.id, name: s.name, modelType}))
      );
    }
    return byType;
  }, [schemasData, ssiData]);

  const typeTreeNodes = useMemo(
    () => buildTypeTreeNodes(getOmniboxAllowedTypes(), schemasByType),
    [schemasByType]
  );

  return {typeTreeNodes};
}
